import React, { Component, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import UniversalPlayerLogin from './pages/UniversalPlayerLogin';
import UnifiedPlayerLayout from './pages/UnifiedPlayerLayout';
import Today from './pages/Today';
import Leaderboard from './pages/Leaderboard';
import Me from './pages/Me';
import AdminLayout from './pages/AdminLayout';
import Roster from './pages/admin/Roster';
import TeamCommunication from './pages/admin/TeamCommunication';
import Drills from './pages/admin/Drills';
import Lessons from './pages/admin/Lessons';
import Challenges from './pages/admin/Challenges';
import Merch from './pages/Merch';
import AdminLeaderboard from './pages/admin/Leaderboard';
import InviteCoach from './pages/admin/InviteCoach';
import Reports from './pages/admin/Reports';
import Upgrade from './pages/admin/Upgrade';
import PlayerProfile from './pages/admin/PlayerProfile';
import AdminBilling from './pages/admin/Billing';
import Library from './pages/admin/Library';
import SuperAdmin from './pages/SuperAdmin';
import SuperLibrary from './pages/SuperLibrary';
import ClubAdmin from './pages/ClubAdmin';
import ClubEditPlayer from './pages/ClubEditPlayer';
import InviteAccept from './pages/InviteAccept';
import ConsentPage from './pages/ConsentPage';
import ParentPortalRequest from './pages/ParentPortalRequest';
import ParentPortal from './pages/ParentPortal';
import PlayerRegister from './pages/PlayerRegister';
import ResetPassword from './pages/ResetPassword';
import ParentLogin from './pages/ParentLogin';
import ParentDashboard from './pages/ParentDashboard';
import ParentOrder from './pages/ParentOrder';
import ParentActivate from './pages/ParentActivate';
import CoachOrder from './pages/CoachOrder';
import ClubOrder from './pages/ClubOrder';
import ParentOnboarding from './pages/ParentOnboarding';
import CoachOnboarding from './pages/CoachOnboarding';
import ClubOnboarding from './pages/ClubOnboarding';
import CoachOnboardingWizard from './pages/CoachOnboardingWizard';
import ParentAccount from './pages/ParentAccount';
import ParentInvite from './pages/ParentInvite';
import StaffAccount from './pages/StaffAccount';
import Faq from './pages/Faq';
import SuperFaq from './pages/SuperFaq';
import SuperAds from './pages/SuperAds';
import SuperPromos from './pages/SuperPromos';
import DemoDrills from './pages/admin/DemoDrills';
import ManageDemos from './pages/admin/ManageDemos';
import Support from './pages/Support';
import SuperEmails from './pages/admin/SuperEmails';
import EmailEditor from './pages/admin/EmailEditor';
import CommunicationPreferences from './pages/CommunicationPreferences';
import Unsubscribe from './pages/Unsubscribe';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('React error boundary caught:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
          <h2>Something went wrong</h2>
          <p style={{ color: '#888', marginBottom: 20 }}>An unexpected error occurred. Please refresh the page.</p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ padding: '10px 24px', fontSize: '1rem', cursor: 'pointer', borderRadius: 6, border: 'none', background: '#f77c00', color: '#fff' }}
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ImpersonationBanner() {
  const { impersonating, endImpersonation } = useAuth();
  const navigate = useNavigate();
  if (!impersonating) return null;

  const handleExit = async () => {
    const dest = impersonating.real_role === 'parent' ? '/parent' : '/super';
    await endImpersonation();
    navigate(dest);
  };

  return (
    <div className="impersonation-banner">
      <span>Viewing as <strong>{impersonating.name}</strong> (read-only)</span>
      <button className="btn btn-sm btn-outline" onClick={handleExit} style={{ marginLeft: 12 }}>Exit</button>
    </div>
  );
}

function StaffRoute({ children, roles }) {
  const { user, loading, impersonating } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) {
    // During impersonation, allow navigation to the impersonated role's pages
    if (impersonating) return children;
    if (user.role === 'player') return <Navigate to="/player" />;
    if (user.role === 'coach') return <Navigate to="/admin" />;
    if (user.role === 'super_admin') return <Navigate to="/super" />;
    if (user.role === 'club_admin') return <Navigate to="/club" />;
    return <Navigate to="/login" />;
  }
  return children;
}

function PlayerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user || user.role !== 'player') return <Navigate to="/player-login" />;
  return children;
}

function ParentRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user || user.role !== 'parent') return <Navigate to="/parent-login" />;
  return children;
}

function LoginRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (user) {
    if (user.role === 'coach') {
      if (user.onboarding_completed === false) return <Navigate to="/admin/setup" />;
      return <Navigate to="/admin" />;
    }
    if (user.role === 'super_admin') return <Navigate to="/super" />;
    if (user.role === 'club_admin') {
      if (user.onboarding_completed === false) return <Navigate to="/club/setup?resume=1" />;
      return <Navigate to="/club" />;
    }
    if (user.role === 'parent') return <Navigate to="/parent" />;
    if (user.role === 'player') return <Navigate to="/player" />;
    return <Navigate to="/" />;
  }
  return <Login />;
}

function TeamRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (user && user.role === 'player') return <Navigate to="/player" />;
  return <Navigate to="/player-login" />;
}

function UpgradeRedirect() {
  const { user, loading } = useAuth();
  const [copied, setCopied] = useState(false);
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'coach') return <Navigate to="/admin/upgrade" replace />;
  if (user.role === 'club_admin') return <Navigate to="/club" replace />;
  if (user.role === 'parent') return <Navigate to="/parent/account" replace />;
  if (user.role === 'super_admin') return <Navigate to="/super" replace />;
  // Player: show message with shareable parent link
  const parentLink = `${window.location.origin}/parent-login`;
  return (
    <div className="page" style={{ textAlign: 'center', paddingTop: 60 }}>
      <h2 style={{ marginBottom: 12 }}>Upgrade Your Account</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>To upgrade, share this link with your parent:</p>
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, wordBreak: 'break-all', fontSize: '0.9rem' }}>
        {parentLink}
      </div>
      <button className="btn btn-orange btn-sm" onClick={() => { navigator.clipboard.writeText(parentLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
        {copied ? 'Copied!' : 'Copy Link'}
      </button>
    </div>
  );
}

function ClubSetupRouter() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (user && user.role === 'club_admin' && searchParams.get('resume') === '1') {
    return <ClubOnboarding />;
  }
  return <CoachOnboarding />;
}

function useDemoTracking() {
  const location = useLocation();
  const { user, teamInfo, activeTeam, apiFetch } = useAuth();
  const lastPath = React.useRef(null);

  React.useEffect(() => {
    if (!user) return;
    const isDemo = (teamInfo && teamInfo.is_demo) || (activeTeam && activeTeam.is_demo);
    if (!isDemo) return;
    if (location.pathname === lastPath.current) return;
    lastPath.current = location.pathname;

    apiFetch('/api/demo/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page_path: location.pathname }),
    }).catch(() => {});
  }, [location.pathname, user, teamInfo, activeTeam, apiFetch]);
}

function AppRoutes() {
  useDemoTracking();

  return (
    <>
      <ImpersonationBanner />
      <Routes>
        <Route path="/login" element={<LoginRedirect />} />
        <Route path="/player-login" element={<UniversalPlayerLogin />} />
        <Route path="/player" element={<PlayerRoute><UnifiedPlayerLayout /></PlayerRoute>}>
          <Route index element={<Today />} />
          <Route path="leaderboard" element={<Leaderboard />} />
          <Route path="me" element={<Me />} />
          <Route path="merch" element={<Merch />} />
          <Route path="faq" element={<Faq audience="player" />} />
        </Route>
        {/* Redirect old routes */}
        <Route path="/t/:joinCode/*" element={<TeamRedirect />} />
        <Route path="/kid-login" element={<Navigate to="/player-login" />} />
        <Route path="/kid/*" element={<Navigate to="/player" />} />
        <Route path="/kid" element={<Navigate to="/player" />} />
        <Route path="/admin/setup" element={<StaffRoute roles={['coach']}><CoachOnboardingWizard /></StaffRoute>} />
        <Route path="/admin" element={<StaffRoute roles={['coach', 'super_admin', 'club_admin']}><AdminLayout /></StaffRoute>}>
          <Route index element={<Roster />} />
          <Route path="communication" element={<TeamCommunication />} />
          <Route path="drills" element={<Drills />} />
          <Route path="seasons" element={<Challenges />} />
          <Route path="leaderboard" element={<AdminLeaderboard />} />
          <Route path="reports" element={<Reports />} />
          <Route path="player/:id" element={<PlayerProfile />} />
          <Route path="coaches" element={<InviteCoach />} />
          <Route path="billing" element={<AdminBilling />} />
          <Route path="upgrade" element={<Upgrade />} />
          <Route path="library" element={<Library />} />
          <Route path="merch" element={<Merch />} />
          <Route path="faq" element={<Faq audience="coach" />} />
          <Route path="account" element={<StaffAccount />} />
        </Route>
        <Route path="/admin/email-preferences" element={<StaffRoute roles={['coach', 'super_admin', 'club_admin']}><CommunicationPreferences /></StaffRoute>} />
        <Route path="/super" element={<StaffRoute roles={['super_admin']}><SuperAdmin /></StaffRoute>} />
        <Route path="/super/lessons" element={<StaffRoute roles={['super_admin']}><Lessons /></StaffRoute>} />
        <Route path="/super/library" element={<StaffRoute roles={['super_admin']}><SuperLibrary /></StaffRoute>} />
        <Route path="/super/faq" element={<StaffRoute roles={['super_admin']}><SuperFaq /></StaffRoute>} />
        <Route path="/super/emails" element={<StaffRoute roles={['super_admin']}><SuperEmails /></StaffRoute>} />
        <Route path="/super/emails/new/:type" element={<StaffRoute roles={['super_admin']}><EmailEditor /></StaffRoute>} />
        <Route path="/super/emails/:id" element={<StaffRoute roles={['super_admin']}><EmailEditor /></StaffRoute>} />
        <Route path="/super/demo-drills" element={<StaffRoute roles={['super_admin']}><DemoDrills /></StaffRoute>} />
        <Route path="/super/demos" element={<StaffRoute roles={['super_admin']}><ManageDemos /></StaffRoute>} />
        <Route path="/super/ads" element={<StaffRoute roles={['super_admin']}><SuperAds /></StaffRoute>} />
        <Route path="/super/promos" element={<StaffRoute roles={['super_admin']}><SuperPromos /></StaffRoute>} />
        <Route path="/super/account" element={<StaffRoute roles={['super_admin']}><StaffAccount /></StaffRoute>} />
        <Route path="/club" element={<StaffRoute roles={['club_admin']}><ClubAdmin /></StaffRoute>} />
        <Route path="/club/players/:id/edit" element={<StaffRoute roles={['club_admin']}><ClubEditPlayer /></StaffRoute>} />
        <Route path="/club/account" element={<StaffRoute roles={['club_admin']}><StaffAccount /></StaffRoute>} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/consent/:token" element={<ConsentPage />} />
        <Route path="/parent-invite/:token" element={<ParentInvite />} />
        <Route path="/register/:joinCode" element={<PlayerRegister />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/parent-portal" element={<ParentPortalRequest />} />
        <Route path="/parent-portal/:token" element={<ParentPortal />} />
        <Route path="/parent-login" element={<ParentLogin />} />
        <Route path="/parent" element={<ParentRoute><ParentDashboard /></ParentRoute>} />
        <Route path="/parent/account" element={<ParentRoute><ParentAccount /></ParentRoute>} />
        <Route path="/parent/merch" element={<ParentRoute><Merch /></ParentRoute>} />
        <Route path="/parent/faq" element={<ParentRoute><Faq audience="parent" /></ParentRoute>} />
        <Route path="/parent/activate" element={<ParentRoute><ParentActivate /></ParentRoute>} />
        <Route path="/parent/communication" element={<ParentRoute><CommunicationPreferences /></ParentRoute>} />
        <Route path="/parents/order-monthly" element={<ParentOrder />} />
        <Route path="/parents/order-yearly" element={<ParentOrder />} />
        <Route path="/parents/onboarding" element={<ParentOnboarding />} />
        <Route path="/parents/setup" element={<ParentOnboarding />} />
        <Route path="/parents/join/:code" element={<ParentOrder />} />
        <Route path="/coaches/order-monthly" element={<CoachOrder />} />
        <Route path="/coaches/order-yearly" element={<CoachOrder />} />
        <Route path="/coaches/onboarding" element={<CoachOnboarding />} />
        <Route path="/coaches/setup" element={<CoachOnboarding />} />
        <Route path="/club/order-monthly" element={<ClubOrder />} />
        <Route path="/club/order-yearly" element={<ClubOrder />} />
        <Route path="/club/onboarding" element={<CoachOnboarding />} />
        <Route path="/club/setup" element={<ClubSetupRouter />} />
        <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
        <Route path="/support" element={<Support />} />
        <Route path="/upgrade" element={<UpgradeRedirect />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
